/**
 * A match's date in the Matches and Dashboard lists. Everyone reads it;
 * Priori staff correct it in place (Ben, 2026-10-24) through the staff
 * match correction (PATCH /api/v1/admin/games/<game_files id>: platform.read,
 * audited old and new, a reason required). The row around it is a link into
 * the match, so every click in here stops at the cell.
 */
import { useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'

import { ApiError, api } from '../lib/api'
import './games.css'

const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation()

/** "2026-08-27 ..." / "2026-08-27" -> "2026-08-27"; anything else -> ''. */
export const isoDay = (raw: string | null) =>
  raw && /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : ''

/** A match day for the lists: "Aug 27, 2026". A CALENDAR day, not an
 *  instant: read and shown in UTC, so it is the same day everywhere — the
 *  old helper made 2026-08-27 00:00 read "Aug 26" west of UTC, beside an
 *  editor showing 27 (SDET review of #896). A bare "2026-08-30" (what a
 *  save writes back) reads like the server's form. */
export function matchDay(raw: string | null) {
  if (!raw) return '—'
  const day = isoDay(raw)
  if (!day) return raw.slice(0, 10)
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('en-US',
    { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export default function MatchDate({ gamefileId, matchDate, label, canEdit, onSaved }: {
  gamefileId: number | null
  /** The match date alone (YYYY-MM-DD), or null when nobody has said — never
   *  the upload date the list falls back to for its label. */
  matchDate: string | null
  /** How the list shows the date. */
  label: string
  canEdit: boolean
  /** The stored date, YYYY-MM-DD (null: cleared). */
  onSaved: (d: string | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!editing) {
    return (
      <span className="gm-date-cell">
        <span>{label}</span>
        {canEdit && gamefileId != null && (
          <button type="button" className="gm-score-edit" aria-label="Edit date"
                  onKeyDown={stop}
                  onClick={(e) => {
                    stop(e)
                    setValue(isoDay(matchDate)); setReason(''); setError(null)
                    setEditing(true)
                  }}>
            edit
          </button>
        )}
      </span>
    )
  }

  const save = async () => {
    if (busy) return                          // one save at a time (SDET #896)
    if (!reason.trim()) { setError('Say why the date is changing.'); return }
    setBusy(true); setError(null)
    try {
      await api(`/api/v1/admin/games/${gamefileId}`, {
        method: 'PATCH',
        body: JSON.stringify({ match_date: value || null, reason: reason.trim() }),
      })
      onSaved(value || null)
      setEditing(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <span className="gm-date-cell" onClick={stop} onKeyDown={(e) => {
      stop(e)
      // Enter saves from the inputs only: on a button it is that button's
      // click (Cancel must not send the PATCH first — SDET review of #896)
      if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') void save()
      if (e.key === 'Escape') { setEditing(false); setError(null) }
    }}>
      <input className="adm-input" type="date" aria-label="Match date" autoFocus
             value={value} onChange={(e) => setValue(e.target.value)} />
      <input className="adm-input" aria-label="Reason for the date change"
             placeholder="why" style={{ width: '9em' }}
             value={reason} onChange={(e) => setReason(e.target.value)} />
      <button type="button" className="adm-btn" disabled={busy || !reason.trim()}
              onClick={() => void save()}>Save</button>
      <button type="button" className="adm-btn adm-btn--quiet"
              onClick={() => { setEditing(false); setError(null) }}>Cancel</button>
      {error && <span className="gm-score-error" role="alert">{error}</span>}
    </span>
  )
}
